import { useTranslation } from "react-i18next";
import { Control, Controller } from "react-hook-form";
import { Checkbox, Link } from "@carbon/react";
import { RegisterFormValues } from "../types";

export interface TermsSectionProps {
  control: Control<RegisterFormValues>;
  /** "BUYER" | "SELLER" — selects which Terms & Conditions PDF the link points at. */
  userType: string;
}

const BUYER_TERMS_URL =
  "https://spectum-docs.s3.sa-east-1.amazonaws.com/TC++Compradores+11.8.pdf";
const SELLER_TERMS_URL =
  "https://spectum-docs.s3.sa-east-1.amazonaws.com/TC+Vendedores+11.8.pdf";
const PRIVACY_URL =
  "https://spectum-docs.s3.sa-east-1.amazonaws.com/Politica+de+Privacidad+Spectum+11.8.pdf";

/**
 * Terms / agreements field group — and THE checkbox-bug fix.
 *
 * Pre-Carbon, `age` and `termsAndConditions` each did `{...register(name, { required: true })}`
 * (which supplies RHF's own `onChange`) and THEN a native `checked={localState}` +
 * `onChange={() => setLocalState(...)}`. The explicit `onChange` shadowed RHF's, so ticking a
 * box updated only local `useState` — RHF's value stayed `false`, the schema's `.oneOf([true])`
 * error never cleared, and **signup was impossible for any real user** (verified live and pinned
 * in e2e/unauthenticated.spec.ts's header comment).
 *
 * Fix: each box is now a `Controller`-bound Carbon `Checkbox` (the `AddressRow` pattern —
 * `field.value` / `field.onChange`), with no shadowing local state. Ticking a box now writes
 * RHF's value, so `.oneOf([true])` clears and submission proceeds. Empty-submit still fails
 * `.oneOf([true])` against the `false` default, so the age/terms error copy still shows — the
 * e2e net's empty-submit assertions stay green; only the checked-box happy path is fixed.
 */
const TermsSection = ({ control, userType }: TermsSectionProps) => {
  const { t } = useTranslation();
  const termsUrl = userType === "BUYER" ? BUYER_TERMS_URL : SELLER_TERMS_URL;

  return (
    <div className="register__terms">
      <Controller
        name="age"
        control={control}
        render={({ field, fieldState }) => (
          <Checkbox
            id="age"
            labelText={t("register.form.age")}
            checked={Boolean(field.value)}
            onChange={(_event, { checked }) => field.onChange(checked)}
            invalid={Boolean(fieldState.error)}
            invalidText={fieldState.error?.message}
          />
        )}
      />

      <Controller
        name="termsAndConditions"
        control={control}
        render={({ field, fieldState }) => (
          <Checkbox
            id="termsAndConditions"
            labelText={
              <>
                {t("register.form.accept")}{" "}
                <Link href={termsUrl} target="_blank" rel="noreferrer">
                  {t("register.form.terms")}
                </Link>
                .
              </>
            }
            checked={Boolean(field.value)}
            onChange={(_event, { checked }) => field.onChange(checked)}
            invalid={Boolean(fieldState.error)}
            invalidText={fieldState.error?.message}
          />
        )}
      />

      <Controller
        name="informationAgreement"
        control={control}
        render={({ field }) => (
          <Checkbox
            id="informationAgreement"
            labelText={t("register.form.info")}
            checked={Boolean(field.value)}
            onChange={(_event, { checked }) => field.onChange(checked)}
          />
        )}
      />

      <p className="register__terms-privacy">
        {t("register.form.please")}{" "}
        <Link href={PRIVACY_URL} target="_blank" rel="noreferrer">
          {t("register.form.privacy")}
        </Link>{" "}
        {t("register.form.personal")}
      </p>
    </div>
  );
};

export default TermsSection;
